import { MouseEvent, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Grid, Hidden, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Portal_FieldsFragment, usePortal_GetAllPortalsQuery } from 'queries';
import { X } from 'ts-react-feather-icons';

import PlusIcon from 'common/icons/plus-icon';
import Modal from 'common/modal';
import Spinner from 'common/spinner';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

const useStyles = makeStyles(() => ({
  title: {
    color: '#096094',
    paddingLeft: '20px',
  },
  button: {
    backgroundColor: '#44D2F2',
    color: '#ffffff',
    display: 'flex',
    float: 'right',
    marginRight: '20px',
  },
  ul: {
    paddingLeft: '0',
  },
  listItem: {
    maxHeight: '60px',
    boxSizing: 'border-box',
    listStyleType: 'none'!,
    margin: '15px',
    boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.25)',
    display: 'inline-block',
    overflow: 'visible',
    cursor: 'pointer',
  },
  itemText: {
    color: '#000000',
    paddingLeft: '10px',
    marginRight: '25px',
    flexWrap: 'nowrap',
    display: 'flex',
    marginTop: '5px',
  },
  xButton: {
    float: 'right',
    transform: 'translate(70%,0%)',
    marginRight: '10px',
  },
  icon: {
    marginRight: '10px',
  },
  portalName: {
    display: 'inline',
    fontWeight: 'bold',
  },
  lineHeight: {
    lineHeight: '43px',
  },
}));

type Props = {
  portals: Portal_FieldsFragment[];
  // eslint-disable-next-line @typescript-eslint/ban-types
  setPortals: Function;
};

export default function ManageAssignedPortals({ portals, setPortals }: Props) {
  const history = useHistory();
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [selectedPortal, setSelectedPortal] = useState<Partial<Portal_FieldsFragment>>({
    id: '',
    name: '',
  });

  const { data: allPortalsData, loading: allPortalsLoading } = usePortal_GetAllPortalsQuery({
    onError: (e) => {
      showAlert({
        message: 'Error getting all portals: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setShowDeleteDialog(false);
    setShowConfirmDialog(false);
    setAnchorEl(null);
  };

  const handleAdd = () => {
    const newPortals = portals || [];

    newPortals.push({
      id: selectedPortal.id,
      name: selectedPortal.name,
    } as Portal_FieldsFragment);

    setPortals(newPortals);
    handleClose();
  };

  const handleRemove = () => {
    setPortals(portals.filter((p) => p.id !== selectedPortal.id));
    handleClose();
  };

  if (!allPortalsData || allPortalsLoading) {
    return <Spinner />;
  }

  const assignedPortalIds = portals.map((portal) => portal.id);
  const portalsForAssignment = (allPortalsData.data || []).filter(
    (portal) => assignedPortalIds.indexOf(portal.id) === -1,
  );

  return (
    <>
      <div>
        <Modal
          setShowModal={setShowConfirmDialog}
          onActionClick={handleAdd}
          onExitClick={handleClose}
          showModal={showConfirmDialog}
          title="Confirm Changes"
          content={
            <Typography>
              Are you sure you want to assign
              <Typography className={classes.portalName}> {selectedPortal.name} </Typography> to
              this account?
            </Typography>
          }
          actionVerb="Confirm"
        />
        <Modal
          setShowModal={setShowDeleteDialog}
          onActionClick={handleRemove}
          onExitClick={handleClose}
          showModal={showDeleteDialog}
          title="Remove Portal?"
          content={
            <Typography>
              Are you sure you want to remove
              <Typography className={classes.portalName}> {selectedPortal.name} </Typography> from
              this account?
            </Typography>
          }
          actionVerb="Confirm"
        />
      </div>
      <Grid container>
        <Grid spacing={3} xs={10} item md={6} lg={8}>
          <Typography variant="h5" className={classes.title}>
            Assigned Portals
          </Typography>
        </Grid>
        <Grid spacing={3} xs={2} item md={6} lg={4}>
          <Button
            className={classes.button}
            onClick={handleClick}
            startIcon={<PlusIcon height="15" width="15" fill="white" />}
          >
            <Hidden mdDown={true}>
              <Typography>Assign a New Portal</Typography>
            </Hidden>
          </Button>
        </Grid>
      </Grid>
      <div>
        <ul className={classes.ul}>
          {portals.map((portal: Portal_FieldsFragment, index: number) => (
            <li
              className={classes.listItem}
              key={portal.name + '-' + index}
              onClick={() => history.push(`/home/${portal.id}/dashboard`)}
            >
              <Grid container xs={12}>
                <Typography className={classes.itemText}>
                  <Grid container xs={2}>
                    <span className={classes.icon}>
                      <img alt="trustate-logo" src="/trustate-logo.svg" />
                    </span>
                  </Grid>
                  <Grid container xs={10} className={classes.lineHeight}>
                    {portal.name}
                  </Grid>
                  <IconButton
                    className={classes.xButton}
                    onClick={(e) => {
                      setSelectedPortal(portal);
                      setShowDeleteDialog(true);
                      e.stopPropagation();
                    }}
                    size="large"
                  >
                    <X />
                  </IconButton>
                </Typography>
              </Grid>
            </li>
          ))}
        </ul>
        <Menu
          id="simple-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          {portalsForAssignment.map((portal: Portal_FieldsFragment) => (
            <MenuItem
              key={portal.id}
              onClick={() => {
                setSelectedPortal(portal);
                setShowConfirmDialog(true);
                setAnchorEl(null);
              }}
            >
              {portal.name}
            </MenuItem>
          ))}
        </Menu>
      </div>
    </>
  );
}
